/**
 * Area Formulas and Geometric Calculation Utilities
 * 
 * This module provides comprehensive geometric calculations for 3D medical imaging:
 * - Area calculation using the shoelace formula
 * - Perimeter calculation for polygons
 * - 3D to 2D plane projection
 * - Self-intersection detection
 * - Depth estimation and volume calculation
 * - Grid generation for visualization
 * 
 * @version 1.0
 */

import { Vector2, Vector3 } from "three";
import { Matrix } from 'ml-matrix';
import PCA from 'pca-js';

/**
 * 3D bounding box interface for depth calculations
 */
interface BoundingBox3D {
    min: Vector3;
    max: Vector3;
    width: number;
    height: number;
    depth: number;
}

/**
 * Result interface for the main shoeStringArea function
 */
interface AreaCalculationResult {
    twoDimPoints: Vector2[];
    area: number;
    convertedPoints: Vector3[];
    normal: Vector3;
    point: Vector3;
    selfIntersecting: boolean;
    perimeter: number;
    gridPoints3D: Vector3[];
}

/**
 * Checks if two line segments intersect using orientation-based algorithm
 * 
 * @param firstStart - Start point of first line segment
 * @param firstEnd - End point of first line segment  
 * @param secondStart - Start point of second line segment
 * @param secondEnd - End point of second line segment
 * @returns True if the line segments intersect
 */
const doLineSegmentsIntersect = (
    firstStart: Vector2, 
    firstEnd: Vector2, 
    secondStart: Vector2, 
    secondEnd: Vector2
): boolean => {
    /**
     * Calculates the orientation of three ordered points using cross product
     * @param first - First point
     * @param second - Second point  
     * @param third - Third point
     * @returns 0 if collinear, 1 if clockwise, 2 if counterclockwise
     */
    const orientation = (first: Vector2, second: Vector2, third: Vector2): number => {
        const val = (second.y - first.y) * (third.x - second.x) - (second.x - first.x) * (third.y - second.y);

        if (val === 0) {
            return 0; // Points are collinear
        }

        return (val > 0) ? 1 : 2; // Clockwise or counterclockwise
    };

    /**
     * Checks if point 'second' lies on line segment 'first-third'
     * @param first - Start point of segment
     * @param second - Point to check
     * @param third - End point of segment
     * @returns True if point lies on the segment
     */
    const onSegment = (first: Vector2, second: Vector2, third: Vector2): boolean => {
        return second.x <= Math.max(first.x, third.x) &&
            second.x >= Math.min(first.x, third.x) &&
            second.y <= Math.max(first.y, third.y) &&
            second.y >= Math.min(first.y, third.y);
    };

    // Calculate orientations for all point combinations
    const firstOrientation = orientation(firstStart, firstEnd, secondStart);
    const secondOrientation = orientation(firstStart, firstEnd, secondEnd);
    const thirdOrientation = orientation(secondStart, secondEnd, firstStart);
    const fourthOrientation = orientation(secondStart, secondEnd, firstEnd);

    // General case: segments intersect if orientations are different
    if (firstOrientation !== secondOrientation && thirdOrientation !== fourthOrientation) {
        return true;
    }

    // Special cases: check if points are collinear and lie on segments
    if (firstOrientation === 0 && onSegment(firstStart, secondStart, firstEnd)) {
        return true;
    }

    if (secondOrientation === 0 && onSegment(firstStart, secondEnd, firstEnd)) {
        return true;
    }

    if (thirdOrientation === 0 && onSegment(secondStart, firstStart, secondEnd)) {
        return true;
    }

    if (fourthOrientation === 0 && onSegment(secondStart, firstEnd, secondEnd)) {
        return true;
    }

    return false;
};

/**
 * Detects if adding the last point to a polygon would create self-intersection
 * This is crucial for maintaining valid polygon geometry in medical imaging
 * 
 * @param points - Array of 2D points forming a polygon
 * @returns True if the polygon would be self-intersecting
 */
export const willBeSelfIntersecting = (points: Vector2[]): boolean => {
    // Need at least 4 points to potentially self-intersect
    if (points.length < 4) {
        return false;
    }

    const lastPoint = points[points.length - 1];
    const secondLastPoint = points[points.length - 2];

    // Check if the new edge intersects with any existing edges (except adjacent ones)
    for (let i = 0; i < points.length - 3; i++) {
        if (doLineSegmentsIntersect(points[i], points[i + 1], secondLastPoint, lastPoint)) {
            return true;
        }
    }

    // Check if closing the polygon would create intersection
    for (let i = 2; i < points.length - 2; i++) {
        if (doLineSegmentsIntersect(points[i], points[i + 1], points[0], lastPoint)) {
            return true;
        }
    }

    return false;
};

/**
 * Finds the best-fit plane for a set of 3D points using Principal Component Analysis
 * This is essential for projecting 3D medical scan data onto a 2D plane for area calculation
 * 
 * @param points - Array of 3D points
 * @returns Object containing the plane normal vector and a point on the plane
 */
export const findBestPlane = (points: Vector3[]): { normal: Vector3; point: Vector3 } => {
    // Convert points to a matrix for PCA analysis
    const data = points.map(p => [p.x, p.y, p.z]);
    const matrix = new Matrix(data);

    // Perform PCA to find principal components
    const centeredMatrix = PCA.computeDeviationMatrix(matrix.to2DArray());
    const deviationScores = PCA.computeDeviationScores(centeredMatrix);
    const eigenVectors = PCA.getEigenVectors(deviationScores);

    // The normal vector to the plane is the third principal component (least variance)
    const normal = eigenVectors[2].vector;

    // The point on the plane is the centroid of all input points
    const centroid = new Vector3(
        matrix.mean('column')[0],
        matrix.mean('column')[1],
        matrix.mean('column')[2]
    );

    const vector3Normal = new Vector3(normal[0], normal[1], normal[2]);
    const point = new Vector3(centroid.x, centroid.y, centroid.z);

    return { normal: vector3Normal, point };
};

/**
 * Projects a 3D point onto a plane defined by a normal vector and a point on the plane
 * 
 * @param point - The 3D point to project
 * @param planeNormal - Normal vector of the plane
 * @param planePoint - A known point on the plane
 * @returns The projected point on the plane
 */
const projectPointOntoPlane = (point: Vector3, planeNormal: Vector3, planePoint: Vector3): Vector3 => {
    const pointToPlane = new Vector3().subVectors(point, planePoint);
    const distance = pointToPlane.dot(planeNormal);
    const projectedPoint = new Vector3().subVectors(point, planeNormal.clone().multiplyScalar(distance));
    return projectedPoint;
};

/**
 * Converts 3D points to 2D coordinates by projecting them onto their best-fit plane
 * This enables 2D geometric calculations on 3D medical scan data
 * 
 * @param points - Array of 3D points to convert
 * @returns Object containing 2D points, converted 3D points, plane normal and point
 */
const convertTo2D = (points: Vector3[]): {
    twoDimPoints: Vector2[];
    convertedPoints: Vector3[];
    normal: Vector3;
    point: Vector3;
} => {
    const { normal, point } = findBestPlane(points);

    // Create two orthogonal vectors on the plane to establish a 2D coordinate system
    const u = new Vector3().crossVectors(normal, new Vector3(0, 0, 1)).normalize();
    const v = new Vector3().crossVectors(normal, u).normalize();

    // Project all points onto the best-fit plane
    const convertedPoints = points.map(currentPoint => {
        return projectPointOntoPlane(currentPoint, normal, point);
    });

    // Convert projected 3D points to 2D coordinates using the plane's coordinate system
    const twoDimPoints = points.map(currentPoint => {
        const projectedPoint = projectPointOntoPlane(currentPoint, normal, point);
        return new Vector2(projectedPoint.dot(u), projectedPoint.dot(v));
    });

    return {
        twoDimPoints,
        convertedPoints,
        normal,
        point,
    };
};

/**
 * Calculates the perimeter of a polygon defined by 3D points
 * 
 * @param points - Array of 3D points forming the polygon
 * @returns Total perimeter length
 */
const calculatePerimeter = (points: Vector3[]): number => {
    let perimeter = 0;

    for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length; // Wrap around to first point
        perimeter += points[i].distanceTo(points[j]);
    }

    return perimeter;
};

/**
 * Creates a 3D bounding box from an array of 3D points
 * Used for determining the dimensions needed for grid generation
 * 
 * @param convertedPoints - Array of 3D points
 * @returns Bounding box object or null if no points provided
 */
const create3DBoundingBox = (convertedPoints: Vector3[]): BoundingBox3D | null => {
    if (convertedPoints.length === 0) {
        return null;
    }

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;

    // Find the minimum and maximum coordinates across all points
    convertedPoints.forEach((point) => {
        if (point.x < minX) minX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.z < minZ) minZ = point.z;

        if (point.x > maxX) maxX = point.x;
        if (point.y > maxY) maxY = point.y;
        if (point.z > maxZ) maxZ = point.z;
    });

    return {
        min: new Vector3(minX, minY, minZ),
        max: new Vector3(maxX, maxY, maxZ),
        width: maxX - minX,
        height: maxY - minY,
        depth: maxZ - minZ,
    };
};

/**
 * Creates a grid of points on a plane for volume estimation and visualization
 * 
 * @param planePoint - A point on the plane
 * @param planeNormal - Normal vector of the plane
 * @param width - Width of the grid
 * @param height - Height of the grid
 * @param resolution - Number of divisions per axis (creates resolution+1 points per axis)
 * @returns Array of 3D grid points
 */
const createGridOnPlane = (
    planePoint: Vector3,
    planeNormal: Vector3,
    width: number,
    height: number,
    resolution: number
): Vector3[] => {
    const gridPoints: Vector3[] = [];

    // Create two orthogonal vectors on the plane to establish coordinate system
    const u = new Vector3().crossVectors(planeNormal, new Vector3(0, 0, 1)).normalize();
    const v = new Vector3().crossVectors(planeNormal, u).normalize();

    // Calculate step sizes for the grid
    const stepX = width / resolution;
    const stepY = height / resolution;

    // Generate grid points in 2D and map them to 3D space
    for (let i = 0; i <= resolution; i++) {
        for (let j = 0; j <= resolution; j++) {
            const x = -width / 2 + i * stepX; // Center the grid around the planePoint
            const y = -height / 2 + j * stepY;

            // Map the 2D grid coordinates to 3D space using the plane's coordinate system
            const uComponent = u.clone().multiplyScalar(x);
            const vComponent = v.clone().multiplyScalar(y);
            const gridPoint3D = planePoint.clone().add(uComponent).add(vComponent);

            gridPoints.push(gridPoint3D);
        }
    }

    return gridPoints;
};

/**
 * Determines if a point is inside a polygon using the ray casting algorithm
 * 
 * @param point - The 2D point to test
 * @param polygon - Array of 2D points defining the polygon
 * @returns True if the point is inside the polygon
 */
const isPointInPolygon = (point: Vector2, polygon: Vector2[]): boolean => {
    let inside = false;

    // Ray casting algorithm: cast a ray from the point to infinity and count intersections
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x, yi = polygon[i].y;
        const xj = polygon[j].x, yj = polygon[j].y;

        // Check if the ray intersects with the edge
        const intersect =
            yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

        if (intersect) {
            inside = !inside; // Toggle inside/outside status
        }
    }

    return inside;
};

/**
 * Filters 3D points to only include those that fall within a 2D polygon when projected onto a plane
 * Used for volume estimation by determining which grid points lie within the measurement area
 * 
 * @param points - Array of 3D points to filter
 * @param polygon - Array of 2D points defining the polygon boundary
 * @param planePoint - A point on the projection plane
 * @param normal - Normal vector of the projection plane
 * @returns Filtered array of 3D points that lie within the polygon
 */
const removeAllPointsOutsidePolygon = (
    points: Vector3[], 
    polygon: Vector2[], 
    planePoint: Vector3, 
    normal: Vector3
): Vector3[] => {
    // Create two orthogonal vectors on the plane for 2D projection
    const u = new Vector3().crossVectors(normal, new Vector3(0, 0, 1)).normalize();
    const v = new Vector3().crossVectors(normal, u).normalize();

    const filteredPoints = points.filter((point) => {
        // Project the 3D point onto the plane
        const projectedPoint = projectPointOntoPlane(point, normal, planePoint);
        
        // Convert projected point to 2D coordinates
        const point2D = new Vector2(
            projectedPoint.dot(u),
            projectedPoint.dot(v)
        );
        
        // Check if the 2D point is inside the polygon
        return isPointInPolygon(point2D, polygon);
    });

    return filteredPoints;
};

/**
 * Calculates the area of a polygon using the shoelace formula (Surveyor's formula)
 * This is the main function for area calculation in 3D medical imaging applications
 * 
 * @param points - Array of 3D points defining the polygon
 * @param useDepth - Whether to generate 3D grid points for volume estimation
 * @returns Comprehensive result object with area, perimeter, and additional geometric data
 */
export const shoeStringArea = (points: Vector3[], useDepth: boolean): AreaCalculationResult => {
    // Validate input: need at least 3 points to form a polygon
    if (points.length < 3) {
        return {
            area: -1,
            convertedPoints: [],
            normal: new Vector3(),
            point: new Vector3(),
            perimeter: -1,
            twoDimPoints: [],
            selfIntersecting: false,
            gridPoints3D: [],
        };
    }

    // Convert 3D points to 2D by projecting onto best-fit plane
    const { twoDimPoints, normal, point, convertedPoints } = convertTo2D(points);

    // Check for self-intersection to validate polygon geometry
    const selfIntersecting = willBeSelfIntersecting(twoDimPoints);

    // Calculate perimeter using original 3D distances
    const perimeter = calculatePerimeter(points);

    // Apply shoelace formula to calculate area in 2D projection
    let area = 0;
    for (let i = 0; i < twoDimPoints.length; i++) {
        const j = (i + 1) % twoDimPoints.length; // Wrap around to first point
        area += twoDimPoints[i].x * twoDimPoints[j].y;
        area -= twoDimPoints[j].x * twoDimPoints[i].y;
    }
    area = Math.abs(area) / 2;

    // If depth analysis is not needed, return basic results
    if (!useDepth) {
        return {
            twoDimPoints,
            area,
            convertedPoints,
            normal,
            point,
            selfIntersecting,
            perimeter,
            gridPoints3D: [],
        };
    }

    // Generate 3D bounding box for grid creation
    const box3D = create3DBoundingBox(convertedPoints);

    if (!box3D) {
        return {
            twoDimPoints,
            area,
            convertedPoints,
            normal,
            point,
            selfIntersecting,
            perimeter,
            gridPoints3D: [],
        };
    }

    // Create a grid of points on the plane for volume estimation
    const gridPointsOnPlane = createGridOnPlane(point, normal, box3D.width, box3D.height, 10);

    // Filter grid points to only include those within the polygon
    const filteredPoints = removeAllPointsOutsidePolygon(gridPointsOnPlane, twoDimPoints, point, normal);

    return {
        twoDimPoints,
        area,
        convertedPoints,
        normal,
        point,
        selfIntersecting,
        perimeter,
        gridPoints3D: filteredPoints,
    };
};